import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp, ShoppingCart, Users, IndianRupee, Package,
  Store, Globe, AlertTriangle, BarChart3
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

export default function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/analytics/overview`, {
        params: { days },
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const formatINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const topStats = analytics ? [
    {
      title: 'Total Orders',
      value: analytics.total_orders || 0,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      testid: 'stat-total-orders',
    },
    {
      title: 'Total Revenue',
      value: formatINR(analytics.total_revenue),
      icon: IndianRupee,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      testid: 'stat-total-revenue',
    },
    {
      title: 'Avg Order Value',
      value: formatINR(analytics.average_order_value),
      icon: TrendingUp,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      testid: 'stat-avg-order',
    },
    {
      title: 'New Customers',
      value: analytics.new_customers || 0,
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      testid: 'stat-new-customers',
    },
  ] : [];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600 mt-1">Online + POS performance, customers, and top products</p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
            data-testid="analytics-period"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        ) : !analytics ? (
          <div className="text-center py-16 text-gray-500">No analytics data available</div>
        ) : (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {topStats.map((s) => {
                const Icon = s.icon;
                return (
                  <Card key={s.title} data-testid={s.testid}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">{s.title}</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
                        </div>
                        <div className={`w-12 h-12 ${s.bg} rounded-xl flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${s.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Online vs POS breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Online */}
              <Card className="border-l-4 border-l-emerald-500">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Globe className="w-5 h-5 text-emerald-600" /> Online Store
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">Orders</span>
                    <span className="text-2xl font-bold">{analytics.online?.orders || 0}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">Revenue</span>
                    <span className="text-xl font-semibold text-emerald-700">
                      {formatINR(analytics.online?.revenue)}
                    </span>
                  </div>
                  {analytics.online?.abandoned > 0 && (
                    <div className="flex items-center justify-between text-sm bg-amber-50 px-3 py-2 rounded-lg">
                      <span className="flex items-center gap-1 text-amber-800">
                        <AlertTriangle className="w-4 h-4" /> Abandoned carts
                      </span>
                      <span className="font-medium text-amber-800">{analytics.online.abandoned}</span>
                    </div>
                  )}
                  {analytics.online?.status_breakdown && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500 mb-2">Status breakdown</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(analytics.online.status_breakdown).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="text-xs">
                            {k}: {v}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* POS */}
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Store className="w-5 h-5 text-amber-600" /> POS (Walk-in)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">Orders</span>
                    <span className="text-2xl font-bold">{analytics.pos?.orders || 0}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">Revenue</span>
                    <span className="text-xl font-semibold text-amber-700">
                      {formatINR(analytics.pos?.revenue)}
                    </span>
                  </div>
                  {analytics.pos?.payment_breakdown &&
                    Object.keys(analytics.pos.payment_breakdown).length > 0 && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-gray-500 mb-2">By payment method</p>
                        <div className="space-y-1">
                          {Object.entries(analytics.pos.payment_breakdown).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                              <span className="capitalize text-gray-700">{k}</span>
                              <span className="font-medium">{formatINR(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            </div>

            {/* Daily Trend Mini Chart */}
            {analytics.daily_trend && analytics.daily_trend.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="w-5 h-5 text-slate-600" /> Daily Revenue Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DailyTrendChart data={analytics.daily_trend} />
                </CardContent>
              </Card>
            )}

            {/* Top Products */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="w-5 h-5 text-slate-600" /> Top Selling Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.top_products && analytics.top_products.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_products.map((p, idx) => (
                      <div
                        key={p.product_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`top-product-${idx}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm flex items-center justify-center shrink-0">
                            #{idx + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.quantity_sold} units sold</p>
                          </div>
                        </div>
                        <span className="font-semibold text-gray-900">{formatINR(p.revenue)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-gray-500">No sales data yet</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function DailyTrendChart({ data }) {
  const max = Math.max(...data.map((d) => d.online + d.pos), 1);
  // Show only the most recent ~30 days
  const points = data.slice(-30);
  return (
    <div className="flex items-end gap-1 h-40 pt-4">
      {points.map((d) => {
        const total = d.online + d.pos;
        const onlinePct = (d.online / max) * 100;
        const posPct = (d.pos / max) * 100;
        return (
          <div
            key={d.date}
            className="flex-1 flex flex-col-reverse items-center group relative"
            title={`${d.date} • Online ₹${Math.round(d.online)} • POS ₹${Math.round(d.pos)}`}
          >
            <div
              className="w-full bg-amber-400 rounded-t-sm"
              style={{ height: `${posPct}%`, minHeight: posPct > 0 ? '2px' : 0 }}
            />
            <div
              className="w-full bg-emerald-500"
              style={{ height: `${onlinePct}%`, minHeight: onlinePct > 0 ? '2px' : 0 }}
            />
            {total > 0 && (
              <span className="absolute -top-5 text-[10px] text-gray-500 opacity-0 group-hover:opacity-100">
                ₹{Math.round(total)}
              </span>
            )}
          </div>
        );
      })}
      <div className="flex flex-col gap-2 ml-3 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-emerald-500 rounded-sm" /> Online
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-amber-400 rounded-sm" /> POS
        </span>
      </div>
    </div>
  );
}
